/**
 * TranscriptCard Component
 * 
 * Individual transcript segment card with timing and text content.
 * Features:
 * - Display transcript text with timing information
 * - Position indicators for debugging
 * - Action buttons (translate, edit, delete)
 * - Smart text content extraction
 */
import React from 'react';
import {
    Card,
    CardContent,
    Box,
    Typography,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Delete,
    Edit
} from '@mui/icons-material';

// Helper function to extract text from combined text using positions
const extractTextFromPosition = (combinedText, leftPos, rightPos) => {
    if (!combinedText || leftPos === undefined || rightPos === undefined) {
        return '';
    }
    return combinedText.substring(leftPos, rightPos);
};

const TranscriptCard = ({ transcript, index, combinedText, onEdit, onDelete, formatTime }) => {
    // Use textContent if available, otherwise fallback to extraction
    const textContent = transcript.textContent || 
        (combinedText && transcript.positions 
            ? extractTextFromPosition(combinedText, transcript.positions.left, transcript.positions.right)
            : (transcript.text?.text || 'Loading text...'));

    return (
        <Card sx={{ mb: 2, border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ pb: 1 }}>
                <Box sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            ({transcript.index + 1}) {formatTime(transcript.start)} - {formatTime(transcript.end)}
                            {transcript.positions && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    [pos: {transcript.positions.left}-{transcript.positions.right}]
                                </Typography>
                            )}
                        </Typography>
                        
                        <Box display="flex" gap={1}>
                            <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => onEdit(transcript)}>
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => onDelete(transcript)} color="error">
                                    <Delete />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    
                    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                        {textContent}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

export default TranscriptCard; 